import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Crown, QrCode, ShoppingBag, Ticket, type LucideIconData } from 'lucide-angular';

import { AuthModalService } from '../../shared/services/auth-modal.service';
import { AuthService } from '../../shared/services/auth.service';
import { BreadcrumbComponent, ButtonComponent, IconComponent, type BreadcrumbItem } from '../../shared/ui';
import { formatCrc } from '../../shared/utils/currency.util';
import { PerronCoinsSettingsService } from './data/perron-coins-settings.service';
import { PERRON_COINS_COPY } from './perron-coins.copy';

/**
 * Página pública `/perron-coins` (A0·web de Perrón Coins, W6): qué son los Coins, cómo se ganan, para qué sirven y su
 * valor FUTURO. SSR (`RenderMode.Server`): los ajustes vienen de `GET /perron-coins/settings` y viajan al cliente por
 * `TransferState` (`PerronCoinsSettingsService`).
 *
 * El bloque del valor tiene tres estados: `undefined` (cargando → esqueleto), `null` (valor desconocido o error → no
 * existe) y el valor, pintado con `formatCrc` (W4). No depende de `modules.coin_payments` (W5): la web aún no conoce
 * ese módulo. En A0 no se pinta ninguna sección de módulos (`activeKnownModules` queda para B1).
 */
@Component({
  selector: 'app-perron-coins-page',
  imports: [BreadcrumbComponent, ButtonComponent, IconComponent, NgOptimizedImage, RouterLink],
  templateUrl: './perron-coins-page.component.html',
  styleUrl: './perron-coins-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerronCoinsPageComponent {
  private readonly authModal = inject(AuthModalService);
  protected readonly isAuthenticated = inject(AuthService).isAuthenticated;

  protected readonly copy = PERRON_COINS_COPY;
  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: PERRON_COINS_COPY.BREADCRUMB_HOME, route: '/' },
    { label: PERRON_COINS_COPY.TITLE, route: null },
  ];

  protected readonly earnWays: readonly { icon: LucideIconData; title: string; text: string }[] = [
    { icon: ShoppingBag, title: PERRON_COINS_COPY.EARN_PURCHASE_TITLE, text: PERRON_COINS_COPY.EARN_PURCHASE_TEXT },
    { icon: Ticket, title: PERRON_COINS_COPY.EARN_CODES_TITLE, text: PERRON_COINS_COPY.EARN_CODES_TEXT },
    { icon: QrCode, title: PERRON_COINS_COPY.EARN_QR_TITLE, text: PERRON_COINS_COPY.EARN_QR_TEXT },
  ];
  protected readonly vipIcon: LucideIconData = Crown;

  /** `undefined` mientras carga; `null` si el valor no se conoce. */
  private readonly settings = toSignal(inject(PerronCoinsSettingsService).get());

  protected readonly valueLoading = computed(() => this.settings() === undefined);
  protected readonly valueText = computed(() => {
    const settings = this.settings();
    return settings ? PERRON_COINS_COPY.VALUE_TEXT.replace('{valor}', formatCrc(settings.coinValueCrc)) : null;
  });

  protected openLogin(): void {
    this.authModal.open('login');
  }
}
